import { StyleSheet } from "react-native";
import {
  radius,
  spacing,
  type FeedbackPriority as FeedbackPriorityT,
  type Palette,
} from "@jackedlogs/shared";
// Platform-correct families (native bare name vs web CSS stack) — the shared
// CSS stack is invalid as an RN fontFamily, see ../theme.
import { platformTypography as typography } from "../theme";

// Priority badge colours, token-driven. p0 reads as danger, p1 as warn,
// p2/none stay quiet. Returns bg + fg so the badge never relies on
// colour alone — the label text (P0/P1/P2/—) carries the meaning too.
export function priorityColors(
  p: Palette,
  pri: FeedbackPriorityT | null,
): { bg: string; fg: string } {
  if (pri === "p0") return { bg: p.dangerFill, fg: p.danger };
  if (pri === "p1") return { bg: p.prFill, fg: p.warn };
  if (pri === "p2") return { bg: p.surface2, fg: p.ink2 };
  return { bg: p.surface2, fg: p.ink3 };
}

const FONT = typography.fonts.sans;
const MONO = typography.fonts.mono;

// Min 44pt tap targets + >=14pt label text throughout — reduced-vision
// readability is a hard constraint (feedback_user_visual_accessibility).
export function makeInboxStyles(p: Palette) {
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: p.bg },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: spacing.s5,
      paddingTop: spacing.s5,
      paddingBottom: spacing.s3,
      gap: spacing.s3,
    },
    title: {
      color: p.ink,
      fontFamily: FONT,
      fontSize: typography.sizes.xl,
      fontWeight: "700",
    },
    countPill: {
      paddingHorizontal: spacing.s4,
      paddingVertical: spacing.s2,
      borderRadius: radius.r4,
      backgroundColor: p.surface2,
    },
    countPillOpen: { backgroundColor: p.brand100 },
    countPillText: {
      color: p.ink2,
      fontFamily: FONT,
      fontSize: typography.sizes.base,
      fontWeight: "600",
    },
    countPillTextOpen: { color: p.brandText },

    filterRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s3,
      paddingHorizontal: spacing.s5,
      paddingBottom: spacing.s4,
    },
    chip: {
      minHeight: 44,
      paddingHorizontal: spacing.s4,
      borderRadius: radius.r4,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
      alignItems: "center",
      justifyContent: "center",
    },
    chipActive: { backgroundColor: p.brand500, borderColor: p.brand500 },
    chipText: {
      color: p.ink2,
      fontFamily: FONT,
      fontSize: typography.sizes.base,
      fontWeight: "600",
    },
    chipTextActive: { color: p.inkOnBrand },

    listContent: { paddingHorizontal: spacing.s5, paddingBottom: spacing.s8 },
    loadMore: { alignSelf: "center", marginTop: spacing.s4, minHeight: 44 },
    stateText: {
      color: p.ink3,
      fontFamily: FONT,
      fontSize: typography.sizes.md,
      padding: spacing.s5,
    },
    errorText: {
      color: p.danger,
      fontFamily: FONT,
      fontSize: typography.sizes.base,
      paddingHorizontal: spacing.s5,
      paddingBottom: spacing.s3,
    },

    row: {
      backgroundColor: p.surface,
      borderRadius: radius.r4,
      borderWidth: 1,
      borderColor: p.border,
      marginBottom: spacing.s3,
      overflow: "hidden",
    },
    rowHead: {
      flexDirection: "column",
      gap: spacing.s2,
      paddingHorizontal: spacing.s4,
      paddingVertical: spacing.s3,
      minHeight: 44,
    },
    rowHeadMeta: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      gap: spacing.s3,
    },
    // Collapsed body preview — the scan target, so full `ink` (matches the
    // expanded bodyText, clears AA on every theme incl. catppuccin-light where
    // ink2 is only 4.06:1) at the ≥14pt floor, truncated to 2 lines.
    snippet: {
      color: p.ink,
      fontFamily: FONT,
      fontSize: typography.sizes.base,
    },
    priBadge: {
      paddingHorizontal: spacing.s3,
      paddingVertical: spacing.s1,
      borderRadius: radius.r3,
      minWidth: 36,
      alignItems: "center",
    },
    priText: {
      fontFamily: MONO,
      fontSize: typography.sizes.sm,
      fontWeight: "700",
    },
    kindText: {
      color: p.ink,
      fontFamily: FONT,
      fontSize: typography.sizes.base,
      fontWeight: "600",
    },
    metaText: {
      color: p.ink2,
      fontFamily: FONT,
      fontSize: typography.sizes.sm,
    },
    emailText: {
      color: p.ink2,
      fontFamily: FONT,
      fontSize: typography.sizes.sm,
    },
    statusChip: {
      borderRadius: radius.r3,
      paddingHorizontal: spacing.s2,
      paddingVertical: 2,
      alignSelf: "center",
    },
    statusChipText: {
      fontFamily: FONT,
      fontSize: typography.sizes.sm,
      fontWeight: "600",
    },

    rowBody: {
      paddingHorizontal: spacing.s4,
      paddingBottom: spacing.s4,
      gap: spacing.s4,
      borderTopWidth: 1,
      borderTopColor: p.border,
      paddingTop: spacing.s4,
    },
    bodyText: {
      color: p.ink,
      fontFamily: MONO,
      fontSize: typography.sizes.base,
      backgroundColor: p.surface2,
      borderRadius: radius.r3,
      padding: spacing.s4,
    },
    controlGroup: { gap: spacing.s3 },
    controlLabel: {
      color: p.ink2,
      fontFamily: FONT,
      fontSize: typography.sizes.sm,
      fontWeight: "600",
    },
    controlRow: { flexDirection: "row", flexWrap: "wrap", gap: spacing.s3 },
  });
}

export type InboxStyles = ReturnType<typeof makeInboxStyles>;
